/** The teacher shell: wordmark, language, sign-out; and, inside a course, the course strip
 * with its three stages so the teacher always knows where they are in the story
 * (plan → items → teach). */
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useSession } from "../auth/SessionContext";
import { LANGS, setLang, type Lang } from "../i18n";
import { capabilities } from "../platform/capabilities";
import { product } from "../product";
import { ZoomControl } from "../ui";

export type Stage = "overview" | "plan" | "items" | "live" | "dashboard";

export function TeacherChrome({
  title,
  children,
  wide = false,
  course,
  stage,
}: {
  title: string;
  children: ReactNode;
  /** Data-dense page (plan, items, teach, progress, reports): the shell uses the whole
   * screen. Without it the shell keeps a reading width for forms and single columns.
   * Neither is a fixed container any more -- see `.shell` in styles.css. */
  wide?: boolean;
  course?: { id: string; title: string } | null;
  stage?: Stage;
}) {
  const { t, i18n } = useTranslation();
  const { principal, signOut } = useSession();
  const tab = "inline-flex min-h-[var(--control-h-sm)] items-center rounded-[var(--radius-control)] px-3 text-sm font-medium hover:bg-[color:var(--surface-2)]";
  const active = "bg-[color:var(--surface)] shadow-sm";
  // Fluid, not fixed: full width with a gutter that grows with the screen, capped far out so
  // a very wide monitor does not stretch a table from bezel to bezel. Long-form text inside a
  // page keeps its measure with `.measure`.
  const shell = ["shell", wide ? "" : "shell-narrow"].join(" ");
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-b border-[color:var(--line)] bg-[color:var(--bg)]">
        <div className={[shell, "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3"].join(" ")}>
          <nav className="flex flex-wrap items-center gap-x-4 gap-y-1" aria-label={t("chrome.nav")}>
            <Link to="/t" className="wordmark text-xl hover:underline" translate="no">{product.name}</Link>
            <Link to="/t" className="text-sm text-[color:var(--ink-2)] hover:underline">{t("teacher.courses")}</Link>
            {capabilities.desktop && <Link to="/t/settings" className="text-sm text-[color:var(--ink-2)] hover:underline">{t("settings.link")}</Link>}
          </nav>
          <div className="flex items-center gap-2 text-sm">
            <ZoomControl />
            <select
              aria-label={t("app.language")}
              className="field"
              value={i18n.language.startsWith("ru") ? "ru" : "en"}
              onChange={(e) => setLang(e.target.value as Lang)}
            >
              {LANGS.map((l) => (
                <option key={l} value={l}>{l.toUpperCase()}</option>
              ))}
            </select>
            {/* On the desktop the principal is the local host identity, and dropping it would
                erase the host token; the cloud sign-out lives in Settings > Account. */}
            {principal && !capabilities.desktop && (
              <button type="button" className="min-h-[var(--control-h)] rounded-[var(--radius-control)] px-2 underline hover:bg-[color:var(--surface-2)]" onClick={signOut}>{t("app.signOut")}</button>
            )}
          </div>
        </div>
        {course && (
          <div className={[shell, "flex flex-wrap items-center justify-between gap-3 pb-3"].join(" ")}>
            <Link to="/t/c/$courseId" params={{ courseId: course.id }} className="display min-w-0 truncate text-lg hover:underline">{course.title}</Link>
            {/* Wraps rather than overflows: a narrow window, or a wide one at 200% interface
                size, still shows all four stages. */}
            <nav className="flex flex-wrap items-center gap-1 rounded-[var(--radius-card)] bg-[color:var(--surface-2)] p-1" aria-label={t("chrome.stages")}>
              <Link to="/t/c/$courseId/plan" params={{ courseId: course.id }} className={[tab, stage === "plan" ? active : ""].join(" ")} aria-current={stage === "plan" ? "page" : undefined}>{t("chrome.plan")}</Link>
              <Link to="/t/c/$courseId/items" params={{ courseId: course.id }} className={[tab, stage === "items" ? active : ""].join(" ")} aria-current={stage === "items" ? "page" : undefined}>{t("chrome.items")}</Link>
              <Link to="/t/c/$courseId" params={{ courseId: course.id }} className={[tab, stage === "overview" || stage === "live" ? active : ""].join(" ")} aria-current={stage === "overview" ? "page" : undefined}>{t("chrome.teach")}</Link>
              <Link to="/t/c/$courseId/dashboard" params={{ courseId: course.id }} className={[tab, stage === "dashboard" ? active : ""].join(" ")} aria-current={stage === "dashboard" ? "page" : undefined}>{t("chrome.dashboard")}</Link>
            </nav>
          </div>
        )}
      </header>
      <div className={[shell, "flex flex-1 flex-col gap-6 py-4 md:py-6"].join(" ")}>
        <h1 className="text-2xl break-words md:text-3xl">{title}</h1>
        <main className="flex flex-1 flex-col gap-6">{children}</main>
      </div>
    </div>
  );
}
